<template>
  <v-dialog v-model="active" width="800">
    <v-card style="height:800px">
      <v-toolbar>
        <v-toolbar-title>{{ name }}</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn icon @click="active = false">
          <v-icon>fal fa-times</v-icon>
        </v-btn>
      </v-toolbar>
      <v-card-text>
        <v-tabs v-model="tab">
          <v-tab>Description</v-tab>
          <v-tab>Performances</v-tab>
          <v-tab>Performers/Discounts</v-tab>
        </v-tabs>
        <v-tabs-items v-model="tab">
          <v-tab-item>
            <v-img v-if="image" :src="`/img/theatre/${showId}.jpg`" height="300">
              <div style="float:right">
                <v-btn color="success" small @click="imageDialog = true">
                  <v-icon left>fas fa-image</v-icon>
                  Change Image
                </v-btn>
                <v-btn color="error" small @click="removeImage()">
                  <v-icon left>fal fa-times</v-icon>
                  Remove Image
                </v-btn>
              </div>
            </v-img>
            <v-btn v-else color="success" @click="imageDialog = true">
              <v-icon left>fas fa-image</v-icon>
              Add Image
            </v-btn>
            <v-dialog v-model="imageDialog" width="350">
              <v-card>
                <v-card-title>Upload Cover Image</v-card-title>
                <v-card-text>
                  <v-file-input v-model="imageUpload" label="Upload Image" accept="image/jpeg" outlined hide-details />
                </v-card-text>
                <v-card-actions>
                  <v-btn text @click="uploadImage">Upload</v-btn>
                  <v-spacer />
                  <v-btn text @click="imageDialog = false">Cancel</v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>
            <html-editor v-model="description" />
            <v-btn @click="saveChanges()">Save Changes</v-btn>
          </v-tab-item>
          <v-tab-item>
            <v-expansion-panels>
              <v-expansion-panel v-for="performance in performances" :key="performance._id">
                <v-expansion-panel-header>
                  <v-row>
                    <v-col :cols="5">{{ performance.dateStr }}</v-col>
                    <v-col>
                      <v-row>
                        <v-col>Seats: {{ performance.tickets }}</v-col>
                        <v-col>Reserved: {{ performance.reserved }}</v-col>
                        <v-col>Seats Left: {{ performance.seatsLeft }}</v-col>
                      </v-row>
                    </v-col>
                  </v-row>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <v-row v-for="{ _id, label, price, limit } in performance.prices" :key="_id">
                    <v-col>
                      <v-combobox :value="label" :items="['Covenant Student', 'Covenant Faculty/Staff', 'Community Adult', 'Community Student/Elder']" label="Price Label" outlined hide-details @change="(label) => updatePrice(performance._id, _id, { label })" />
                    </v-col>
                    <v-col>
                      <v-text-field :value="price" label="Price Amount" type="number" outlined hide-details @change="(price) => updatePrice(performance._id, _id, { price })" />
                    </v-col>
                    <v-col>
                      <v-text-field :value="limit" label="Ticket Limit" type="number" hint="Leave blank for no limit" outlined hide-details clearable @change="(limit) => updatePrice(performance._id, _id, { limit })" />
                    </v-col>
                  </v-row>
                  <v-btn class="mt-4" @click="addPrice(performance._id)">Add Price Option</v-btn>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </v-tab-item>
          <v-tab-item>
            <v-data-table :items="discounts" :headers="discountHeaders">
              <template #item.used="{ item }">
                <v-icon :color="item.used ? 'success' : 'error'">fas fa-{{ item.used ? 'check' : 'minus' }}</v-icon>
              </template>
              <template #item.action="{ item }">
                <v-btn color="error" style="min-width:24px" @click="removeDiscount(item)">
                  <v-icon>fal fa-trash-alt</v-icon>
                </v-btn>
              </template>
            </v-data-table>
            <v-dialog v-model="discountDialog" persistent width="800">
              <template #activator="{ on }">
                <v-btn v-on="on">Add Discount</v-btn>
              </template>
              <v-card>
                <v-card-title>Add Discount</v-card-title>
                <v-card-text>
                  <p>Discounts are added via email address. An email will be sent to them with the information on how to use the discount. Students/Employees with a discount code can use it when they log in, or the email will also include a code that they can send to someone else to use (like parents).</p>
                  <v-row>
                    <v-col>
                      <v-switch v-model="discountCovenant" :label="`User ${discountCovenant ? 'has' : 'doesn\'t have'} a Covenant account`" />
                      <directory-search v-if="discountCovenant" v-model="discountUser" label="Directory Search" :person-types="['Faculty','Staff','Student']" dense />
                      <v-text-field v-else v-model="discountEmail" label="Non-Covenant Email" outlined dense />
                      <v-text-field v-model="discountPrice" label="Discounted Price" type="number" min="0" outlined dense />
                      <v-text-field v-model="discountLimit" label="Ticket Limit" type="number" hint="How many at this price" min="1" clearable persistent-hint outlined dense />
                    </v-col>
                    <v-col>
                      <h4>Eligible Performances</h4>
                      <v-list dense class="py-0">
                        <v-list-item v-for="{ dateStr, _id } of performances" :key="_id" @click="toggleDiscountPerformance(_id)">
                          <v-list-item-action style="max-height:16px;margin-right:12px">
                            <v-icon>fal fa-{{ discountPerformances.includes(_id) ? 'check-' : '' }}square</v-icon>
                          </v-list-item-action>
                          <v-list-item-title>{{ dateStr }}</v-list-item-title>
                        </v-list-item>
                      </v-list>
                      <v-btn dense :disabled="performances.length === discountPerformances.length" @click="discountPerformances = performances.map(({ _id }) => _id)">Select All</v-btn>
                      <v-btn dense :disabled="discountPerformances.length === 0" @click="discountPerformances = []">Deselect All</v-btn>
                    </v-col>
                  </v-row>
                </v-card-text>
                <v-card-actions>
                  <v-btn :disabled="!validDiscount" @click="addDiscount">Add Discount</v-btn>
                  <v-spacer />
                  <v-btn @click="discountDialog = false">Close</v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>
          </v-tab-item>
        </v-tabs-items>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>
<script>
import { ref, computed, watch } from '@vue/composition-api'
import axios from 'axios'

export default {
  components: {
    HtmlEditor: () => import('@/components/forms/HTMLEditor'),
    DirectorySearch: () => import('@/components/forms/inputs/DirectorySearch')
    // DatePicker: () => import('@/components/forms/inputs/DatePicker'),
    // TimePicker: () => import('@/components/forms/inputs/TimePicker')
  },
  props: {
    showId: { type: String, required: true },
    value: Boolean
  },
  setup (props, { root, emit }) {
    const user = computed(() => 'spoof' in root.$store.state.user ? root.$store.state.user.spoof : root.$store.state.user)
    const isAdmin = computed(() => user.value.roles.includes('Technology Services'))

    const active = ref(false)

    watch(() => props.value, () => {
      console.log(`props.value changed: ${props.value}`)
      if (props.value !== active.value) active.value = props.value
    })
    watch(active, () => {
      if (props.value !== active.value) emit('input', active.value)
    })

    const tab = ref(0)
    const name = ref('')
    const year = ref('')
    const shortName = ref('')
    const showHeader = ref(true)
    const image = ref(true)
    const description = ref('')
    const performances = ref([])
    const isLoaded = ref(false)

    function parseRes (res) {
      name.value = res.name
      year.value = res.year
      shortName.value = res.shortName
      showHeader.value = res.showHeader
      image.value = res.image == null ? true : res.image
      description.value = res.description
      performances.value = res.performances.map((row) => {
        const dt = new Date(row.date)
        const date = dt.toLocaleDateString('en-US', { weekday: 'short', year: 'numeric', month: 'long', day: 'numeric' })
        const time = dt.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'America/New_York' })
        row.dateStr = `${date} ${time}`
        row.seatsLeft = row.tickets - row.reserved
        return row
      }).sort(({ date: aDate }, { date: bDate }) => new Date(aDate) < new Date(bDate) ? -1 : 1)
      discounts.value = res.discounts || []
    }

    watch(() => props.showId, async () => {
      if (props.showId) {
        const res = await root.$feathers.service('theatre/show').get(props.showId)
        parseRes(res)
        setTimeout(() => { isLoaded.value = true }, 200)
      }
    })

    watch(name, () => {
      if (isLoaded.value) patch({ name: name.value })
    })
    watch(shortName, () => {
      if (isLoaded.value) patch({ shortName: shortName.value })
    })
    watch(description, () => {
      if (isLoaded.value) patch({ description: description.value })
    })

    async function patch (patch) {
      const res = await root.$feathers.service('theatre/show').patch(props.showId, patch)
      emit('update', res)
      return res
    }

    const imageDialog = ref(false)
    const imageUpload = ref(null)
    async function uploadImage () {
      if (imageUpload.value) {
        // Upload the file
        const formData = new FormData()
        formData.append('folder', 'theatre')
        formData.append('filename', props.showId)
        formData.append('file', imageUpload.value)

        image.value = true
        patch({ image: true })
        const token = window.localStorage['feathers-jwt']
        const basePath = process.env.VUE_APP_API_URL + process.env.VUE_APP_API_PATH.replace('/socket.io', '')
        imageDialog.value = false
        await axios.post(basePath + '/system/image-upload', formData, { headers: { 'Content-Type': 'multipart/form-data', 'Authorization': `Bearer ${token}` } })
      }
    }

    function removeImage () {
      image.value = false
      patch({ image: false })
    }

    async function updatePrice (performanceId, priceId, upd) {
      const patch = {}
      for (const field in upd) {
        patch[`performances.$[perf].prices.$[price].${field}`] = upd[field]
      }
      const arrayFilters = [{ 'perf._id': performanceId }, { 'price._id': priceId }]
      const res = await root.$feathers.service('theatre/show').patch(props.showId, patch, { query: { arrayFilters } })
      parseRes(res)
    }

    async function addPrice (performanceId) {
      console.log(`Adding price for performance ${performanceId}`)
      const patch = { $push: { 'performances.$[perf].prices': { label: '', price: 0 } } }
      const arrayFilters = [{ 'perf._id': performanceId }]
      const res = await root.$feathers.service('theatre/show').patch(props.showId, patch, { query: { arrayFilters } })
      parseRes(res)
    }

    const discounts = ref([])
    const discountHeaders = ref([
      { text: 'Email', value: 'email' },
      { text: 'Code', value: 'code' },
      { text: 'Discounted Price', value: 'discountedPrice' },
      { text: 'Limit', value: 'limit' },
      { text: 'Used', value: 'used' },
      { text: 'Remove', value: 'action' }
    ])

    async function removeDiscount ({ _id }) {
      const res = await root.$feathers.service('theatre/show').patch(props.showId, { $pull: { 'discounts': { _id } } })
      parseRes(res)
    }

    const discountDialog = ref(false)
    const discountCovenant = ref(true)
    const discountUser = ref(null)
    const discountEmail = ref('')
    const discountPrice = ref('0')
    const discountLimit = ref('2')
    const discountPerformances = ref([])
    const discountPerformanceOptions = computed(() => performances.value.map(({ dateStr, _id }) => { return { text: dateStr, value: _id } }))

    const validDiscount = computed(() => {
      if ((discountCovenant.value && !discountUser.value) || (!discountCovenant.value && !discountEmail.value) || discountPerformances.value.length === 0 || (!discountPrice.value)) return false
      if (!discountCovenant.value) {
        const regex = /^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/
        return regex.test(discountEmail.value)
      }
      return true
    })

    function toggleDiscountPerformance (_id) {
      const index = discountPerformances.value.findIndex((id) => id === _id)
      if (index >= 0) {
        discountPerformances.value.splice(index, 1)
      } else {
        discountPerformances.value.push(_id)
      }
    }

    async function removeDiscountUser (email) {
      console.log(email)
    }
    async function addDiscount () {
      const code = await generateDiscountCode()
      const obj = {
        discountedPrice: discountPrice.value,
        limit: discountLimit.value,
        performances: discountPerformances.value,
        code
      }
      if (discountCovenant.value) {
        const res = await root.$feathers.service('directory/people').get(discountUser.value)
        if (res && res.email) obj.email = res.email
      } else {
        obj.email = discountEmail.value
      }
      const res = await root.$feathers.service('theatre/show').patch(props.showId, { $push: { discounts: obj } })
      parseRes(res)
      discountUser.value = ''
      discountEmail.value = ''
    }

    async function generateDiscountCode () {
      const code = Math.random().toString(36).substring(2, 10).toUpperCase()
      const { total } = root.$feathers.service('theatre/show').find({ query: { aggregate: [{ $match: { 'discountCodes.code': code, year: year.value } }] } })
      if (total > 0) {
        return generateDiscountCode()
      }
      return code
    }

    return {
      user,
      isAdmin,
      active,
      tab,
      name,
      shortName,
      showHeader,
      description,
      performances,
      isLoaded,
      image,
      imageDialog,
      imageUpload,
      uploadImage,
      removeImage,
      updatePrice,
      addPrice,
      discounts,
      discountHeaders,
      removeDiscount,
      discountDialog,
      discountCovenant,
      discountUser,
      discountEmail,
      discountPrice,
      discountLimit,
      discountPerformances,
      discountPerformanceOptions,
      validDiscount,
      toggleDiscountPerformance,
      removeDiscountUser,
      addDiscount
    }
  }
}
</script>
